/**
 * 学习乐园 · 24 点（game-24dian.js）
 * ============================================================
 * 「逻辑与推理」组第四作：发几张牌，两两合成一个数，凑出 24。
 *   交互：点一张牌 → 点一个运算符 → 点另一张牌 → 两张合一；
 *   合出的新牌带着自己的算式（如 (3+5)），最后剩一张且等于 24 即通关。
 *
 * 反馈底线（同本栏目所有游戏）：
 *   每次合成把「谁和谁、怎么算、得多少」摆进步骤表——24 点的运算顺序
 *   就是两两合一的先后，先算的先括起来，步骤表本身就是示范；
 *   除不尽照除（24 点允许分数，分数用分子/分母如实显示）、减出负数
 *   不犯规、除数是 0 讲清「没有意义」拦下不执行；
 *   「看一种解法」如实亮出求解器的路径并讲运算顺序，看解法的局不进成绩。
 *
 * 口径（页面与 doc 页同步说明）：
 *   - 档位：入门 3 个数（1–12，两步合一片）/ 经典 4 个数（1–10）/
 *     挑战 4 个数（1–13，只发「可行路径少」的窄路题）；
 *   - 每一局都由本机求解器穷举验证过必有解——你卡住不是题目无解；
 *   - 数字按扑克牌口径 A=1，直接写 11/12/13 不摆人头牌（记号不是考点）；
 *   - 不计时排名：这游戏考的是算得通不是算得快，成绩只记完成局数；
 *   - 结算时报「本手牌共 N 条可行路径」：穷举口径 = 两两组合 × 四则运算，
 *     3+5 与 5+3 算两条（顺序不同），不含数学等价折算。
 *
 * 简化红线：不做中断续局，刷新即重开；成绩只存本机 localStorage，不上传。
 *
 * 冒烟钩子：window.ST24 暴露纯引擎函数（deal / solve / countPaths /
 * apply / exprOf / fmt / validateNums），供确定性用例；交互路径由
 * scripts/smoke-study.mjs 的对应 section 走真实点击。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-24dian';
  var RECORD_KEY = 'study-24dian-record';

  /* ============ 有理数（分子/分母，归约到最简，分母恒正） ============ */

  function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { var t = a % b; a = b; b = t; } return a || 1; }
  function rat(n, d) {
    if (d < 0) { n = -n; d = -d; }
    var g = gcd(n, d);
    return { n: n / g, d: d / g };
  }
  function isZero(r) { return r.n === 0; }
  function eq24(r) { return r.d > 0 && r.n === 24 * r.d; }
  /** 显示口径：整数直接写，分数写「分子/分母」（负号随分子） */
  function fmt(r) { return r.d === 1 ? String(r.n) : r.n + '/' + r.d; }

  /** 四则运算：除数为 0 返回 null（调用方讲原因拦下），其余返回最简分数 */
  function apply(op, a, b) {
    switch (op) {
      case '+': return rat(a.n * b.d + b.n * a.d, a.d * b.d);
      case '−': return rat(a.n * b.d - b.n * a.d, a.d * b.d);
      case '×': return rat(a.n * b.n, a.d * b.d);
      case '÷': return isZero(b) ? null : rat(a.n * b.d, a.d * b.n);
    }
    return null;
  }

  /* ============ 算式拼装（最少括号，括号即运算顺序的教学点） ============ */

  var PREC = { '+': 1, '−': 1, '×': 2, '÷': 2, num: 3 };

  /** 把 a op b 拼成算式：子式优先级低就括起来；右孩子同级且遇 − ÷ 也括
      （a−(b−c)≠a−b−c、a÷(b×c)≠a÷b×c），同级 + × 则可省（结果相同）。 */
  function exprOf(a, op, b) {
    var p = PREC[op];
    var l = a.prec < p ? '(' + a.expr + ')' : a.expr;
    var r = (b.prec < p || (b.prec === p && (op === '−' || op === '÷')))
      ? '(' + b.expr + ')' : b.expr;
    return l + op + r;
  }

  /* ============ 求解器（穷举两两组合，路径即运算顺序） ============ */

  /** 候选运算序：a+b、a×b 只试一次（交换律），减除各试两个方向 */
  var OP_PAIRS = ['+', '×', '−', '÷'];

  /**
   * 解一手牌：返回第一步就成功的完整合成步骤（从发牌口径）。
   * 每步 {a, op, b, r}，a/b/r 为 {n,d,expr,prec}。无解返回 null。
   * items 数组元素即「牌」：数字牌 expr 为数字字符串，合成的牌带算式。
   */
  function search(items) {
    if (items.length === 1) return eq24(items[0].v) ? [] : null;
    for (var i = 0; i < items.length; i++) {
      for (var j = i + 1; j < items.length; j++) {
        var A = items[i], B = items[j];
        var rest = [];
        for (var k = 0; k < items.length; k++) if (k !== i && k !== j) rest.push(items[k]);
        for (var oi = 0; oi < OP_PAIRS.length; oi++) {
          var orders = [A, B];
          for (var dir = 0; dir < (OP_PAIRS[oi] === '+' || OP_PAIRS[oi] === '×' ? 1 : 2); dir++) {
            var a = orders[dir], b = orders[dir === 0 ? 1 : 0];
            var r = apply(OP_PAIRS[oi], a.v, b.v);
            if (r === null) continue;
            /* 算式必须按实际运算方向拼（反向时是 B op A），否则算式与值对不上 */
            var card = { v: r, expr: exprOf(a, OP_PAIRS[oi], b), prec: PREC[OP_PAIRS[oi]] };
            var sub = search(rest.concat([card]));
            if (sub !== null) {
              return [{ a: { v: a.v, expr: a.expr }, op: OP_PAIRS[oi], b: { v: b.v, expr: b.expr }, r: r, expr: card.expr }].concat(sub);
            }
          }
        }
      }
    }
    return null;
  }

  /** 对外 solve：整数数组 → 步骤数组或 null（验证器与「看解法」共用） */
  function solve(nums) {
    var items = nums.map(function (n) { return { v: rat(n, 1), expr: String(n), prec: PREC.num }; });
    return search(items);
  }

  /**
   * 可行路径数：穷举所有「两两组合 × 四则」的合成顺序，数能凑到 24 的
   * 条数（3+5 与 5+3 算两条，不折算数学等价）。4 个数上限 3888 条，
   * 纯数值计数不建算式，毫秒级——挑战档据此挑「窄路题」。
   */
  function countAll(items) {
    if (items.length === 1) return eq24(items[0].v) ? 1 : 0;
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      for (var j = i + 1; j < items.length; j++) {
        var A = items[i], B = items[j];
        var rest = [];
        for (var k = 0; k < items.length; k++) if (k !== i && k !== j) rest.push(items[k]);
        for (var oi = 0; oi < OP_PAIRS.length; oi++) {
          var orders = [A, B];
          for (var dir = 0; dir < (OP_PAIRS[oi] === '+' || OP_PAIRS[oi] === '×' ? 1 : 2); dir++) {
            var a = orders[dir], b = orders[dir === 0 ? 1 : 0];
            var r = apply(OP_PAIRS[oi], a.v, b.v);
            if (r === null) continue;
            total += countAll(rest.concat([{ v: r }]));
          }
        }
      }
    }
    return total;
  }
  function countPaths(nums) {
    return countAll(nums.map(function (n) { return { v: rat(n, 1) }; }));
  }

  /* ============ 发牌（保证有解；挑战档保证窄路） ============ */

  var LEVELS = {
    1: { name: '入门 3 数', count: 3, max: 12, narrow: false, fallback: [2, 3, 4],
         hint: '两步合一片，先把交互走顺' },
    2: { name: '经典 4 数', count: 4, max: 10, narrow: false, fallback: [1, 2, 3, 4],
         hint: '四数凑 24 的经典口径' },
    3: { name: '挑战 4 数', count: 4, max: 13, narrow: true, fallback: [3, 3, 8, 8],
         hint: '只发可行路径 ≤ 10 条的窄路题' },
  };
  var NARROW_MAX = 10;

  function rnd(max) { return 1 + Math.floor(Math.random() * max); }

  /** 抽一手：必经求解器验证有解（挑战档再要求窄路），兜底用手工题 */
  function deal(level) {
    var spec = LEVELS[level] || LEVELS[2];
    for (var tries = 0; tries < 500; tries++) {
      var hand = [];
      for (var i = 0; i < spec.count; i++) hand.push(rnd(spec.max));
      if (solve(hand) === null) continue;
      if (spec.narrow && countPaths(hand) > NARROW_MAX) continue;
      return hand;
    }
    return spec.fallback.slice();
  }

  /* ============ 验证器输入校验（字符级讲原因，不是报错） ============ */

  function validateNums(parts) {
    var nums = [];
    for (var i = 0; i < 4; i++) {
      var s = String(parts[i] == null ? '' : parts[i]).trim();
      if (!s.length) return { ok: false, why: '第 ' + (i + 1) + ' 个数还没填。' };
      if (!/^\d+$/.test(s)) return { ok: false, why: '第 ' + (i + 1) + ' 个数「' + s + '」不是正整数。' };
      var n = Number(s);
      if (n < 1 || n > 99) return { ok: false, why: '第 ' + (i + 1) + ' 个数要在 1 到 99 之间。' };
      nums.push(n);
    }
    return { ok: true, nums: nums, why: '' };
  }

  /* ============ 状态与工具 ============ */

  var root = null;
  var ui = {};
  var S = {
    level: 1, hand: [], cards: [], steps: [], undos: 0, pathCount: 0,
    selCard: -1, selOp: '', done: false, gaveUp: false, fracTaught: false, negTaught: false,
    soundOn: true, resetArm: false, resetTimer: null,
  };

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* —— 音效（WebAudio 本地合成，与组内三作同一套音色思路）—— */
  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'ok') seq = [[700, 0]];
      else if (kind === 'bad') seq = [[210, 0]];
      else seq = [[523, 0], [659, 0.09], [784, 0.18]];   /* win：三连琶音 */
      var type = kind === 'bad' ? 'square' : 'sine';
      for (var i = 0; i < seq.length; i++) {
        var osc = actx.createOscillator();
        var g = actx.createGain();
        osc.type = type;
        osc.frequency.value = SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0];
        var t0 = t + seq[i][1];
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.27 : 0.09, t0 + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + (kind === 'win' && i === seq.length - 1 ? (SFX_MOBILE ? 0.32 : 0.24) : (SFX_MOBILE ? 0.16 : 0.1)));
        osc.connect(g).connect(actx.destination);
        osc.start(t0); osc.stop(t0 + (SFX_MOBILE ? 0.4 : 0.3));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  /* —— 偏好与成绩（只存本机）—— */
  function save() {
    try { localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, level: S.level, soundOn: S.soundOn })); }
    catch (e) { /* 存储不可用静默（无痕模式） */ }
  }
  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (d && d.v === 1) {
        if (LEVELS[d.level]) S.level = d.level;
        S.soundOn = d.soundOn !== false;
      }
    } catch (e) { /* 忽略脏数据 */ }
  }
  function loadRecord() {
    try { return JSON.parse(localStorage.getItem(RECORD_KEY) || '{}') || {}; }
    catch (e) { return {}; }
  }
  function saveRecord(rec) {
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 静默 */ }
  }

  /* ============ 渲染 ============ */

  function renderStatus(msg) {
    if (msg != null) { ui.status.textContent = msg; return; }
    if (S.done) return;
    ui.status.textContent = '剩 ' + S.cards.length + ' 张牌 · 已合 ' + S.steps.length + ' 步'
      + (S.undos ? ' · 撤销 ' + S.undos + ' 次' : '');
  }

  function renderCards() {
    ui.board.textContent = '';
    S.cards.forEach(function (c, idx) {
      var card = el('button', 'st-d24-card'
        + (c.v.d !== 1 ? ' is-frac' : '')
        + (idx === S.selCard ? ' is-sel' : ''));
      card.type = 'button';
      card.setAttribute('data-idx', String(idx));
      card.setAttribute('data-val', fmt(c.v));
      card.setAttribute('data-expr', c.expr);
      card.setAttribute('aria-pressed', idx === S.selCard ? 'true' : 'false');
      card.setAttribute('aria-label', '牌：值为 ' + fmt(c.v)
        + (c.expr !== fmt(c.v) ? '，由算式 ' + c.expr + ' 合成' : ''));
      card.appendChild(el('span', 'st-d24-val', fmt(c.v)));
      if (c.expr !== fmt(c.v)) card.appendChild(el('span', 'st-d24-expr', '= ' + c.expr));
      card.addEventListener('click', function () { onCard(idx); });
      ui.board.appendChild(card);
    });
  }

  function renderOps() {
    for (var i = 0; i < ui.ops.length; i++) {
      var b = ui.ops[i];
      var op = b.getAttribute('data-op');
      b.classList.toggle('is-active', S.selOp === op);
      b.setAttribute('aria-checked', S.selOp === op ? 'true' : 'false');
      b.disabled = S.done || S.selCard < 0 || S.cards.length < 2;
    }
  }

  function renderSteps() {
    ui.steps.textContent = '';
    if (!S.steps.length) { ui.stepsWrap.hidden = true; return; }
    ui.stepsWrap.hidden = false;
    S.steps.forEach(function (st, i) {
      ui.steps.appendChild(el('li', 'st-d24-step' + (i === S.steps.length - 1 ? ' is-last' : ''),
        '第 ' + (i + 1) + ' 步：' + st.text));
    });
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord();
    var any = LEVELS[1] && rec[1] && rec[1].plays || rec[2] && rec[2].plays || rec[3] && rec[3].plays;
    if (!any) {
      box.className = 'st-llk-record is-empty';
      box.appendChild(el('p', 'st-llk-record-empty', '还没算出过 24——凑成一手，这里按档记下完成局数。'));
      return;
    }
    box.className = 'st-llk-record';
    [1, 2, 3].forEach(function (lv) {
      var r = rec[lv];
      box.appendChild(recordStat(LEVELS[lv].name, r && r.plays ? r.plays + ' 局' : '—'));
    });
  }

  function renderButtons() {
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置成绩';
    ui.undoBtn.disabled = S.done || !S.steps.length;
    ui.solutionBtn.disabled = S.done;
  }

  function renderLevels() {
    ui.levels.textContent = '';
    [1, 2, 3].forEach(function (lv) {
      var b = el('button', 'st-llk-pack-btn' + (S.level === lv ? ' is-active' : ''));
      b.type = 'button';
      b.setAttribute('data-level', String(lv));
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', S.level === lv ? 'true' : 'false');
      b.textContent = LEVELS[lv].name;
      b.addEventListener('click', function () {
        if (S.level === lv) return;
        S.level = lv;
        renderLevels();
        newGame();
        save();
      });
      ui.levels.appendChild(b);
    });
  }

  function renderNote(msg, tone) {
    ui.note.textContent = msg;
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  /* ============ 交互 ============ */

  function onCard(idx) {
    if (S.done || S.cards.length < 2) return;
    if (S.selCard === idx) {           /* 点同一张 = 取消选中 */
      S.selCard = -1;
      S.selOp = '';
      renderCards(); renderOps();
      return;
    }
    if (S.selCard >= 0 && S.selOp) {   /* 点第二张 → 合成 */
      combine(S.selCard, S.selOp, idx);
      return;
    }
    S.selCard = idx;                   /* 选中 / 换选 */
    renderCards(); renderOps();
    renderNote(S.cards[idx].expr === fmt(S.cards[idx].v)
      ? '选中 ' + fmt(S.cards[idx].v) + '——再点一个运算符。'
      : '选中 ' + fmt(S.cards[idx].v) + '（' + S.cards[idx].expr + '）——再点一个运算符。', '');
  }

  function combine(i, op, j) {
    var a = S.cards[i], b = S.cards[j];
    var r = apply(op, a.v, b.v);
    if (r === null) {
      renderNote('除数是 0——任何数除以 0 都没有意义，这一步不成立。换一个数来除，或者换一个运算。', 'warn');
      sfx('bad');
      return;
    }
    var text = fmt(a.v) + ' ' + op + ' ' + fmt(b.v) + ' = ' + fmt(r);
    var explain = '';
    if (op === '÷' && b.v.d === 1 && b.v.n !== 0 && r.d !== 1 && !S.fracTaught) {
      explain = '除不尽也照除：24 点允许分数，' + fmt(a.v) + '÷' + fmt(b.v) + ' = ' + fmt(r)
        + '，中间结果是分数不犯规，最后凑得出 24 就是正经解法。';
      S.fracTaught = true;
    } else if (op === '÷' && (b.v.d !== 1 || b.v.n !== 0) && r.d !== 1 && !S.fracTaught) {
      explain = '分数继续除也行：' + fmt(a.v) + '÷' + fmt(b.v) + ' = ' + fmt(r) + '。';
      S.fracTaught = true;
    } else if (op === '−' && r.n < 0 && !S.negTaught) {
      explain = '减出负数不犯规：' + fmt(a.v) + '−' + fmt(b.v) + ' = ' + fmt(r)
        + '，中间是负数没关系，最后凑回 24 就行。';
      S.negTaught = true;
    }
    /* 合成：新牌插到被吃两牌中较前的位置，顺序稳定便于对照 */
    var snapshot = S.cards.slice();   /* 撤销用：合成前的整面牌序 */
    var expr = exprOf({ expr: a.expr, prec: a.prec }, op, { expr: b.expr, prec: b.prec });
    var card = { v: r, expr: expr, prec: PREC[op] };
    var lo = Math.min(i, j), hi = Math.max(i, j);
    S.cards.splice(lo, 1); S.cards.splice(hi - 1, 1);
    S.cards.splice(lo, 0, card);
    S.steps.push({ text: text, explain: explain, snapshot: snapshot });
    S.selCard = -1; S.selOp = '';
    sfx('ok');
    if (explain) renderNote(explain, ''); else renderNote(text + '——已合成一张新牌。', '');
    if (S.cards.length === 1) settle(card, text);
    else { renderCards(); renderOps(); renderSteps(); renderStatus(); renderButtons(); }
  }

  function settle(finalCard, lastText) {
    S.done = true;
    if (eq24(finalCard.v)) {
      var rec = loadRecord();
      var r = rec[S.level] || { plays: 0 };
      r.plays++;
      rec[S.level] = r;
      saveRecord(rec);
      ui.status.textContent = '算出来了！' + S.steps.length + ' 步合成 24'
        + (S.undos ? '（撤销 ' + S.undos + ' 次）' : '');
      ui.status.className = 'st-status is-over';
      renderNote('最后一步 ' + lastText + '——整条算式 ' + finalCard.expr + ' = 24。'
        + '这手牌按穷举口径共有 ' + S.pathCount + ' 条可行路径，你走通了自己那条。'
        + '运算顺序就是合成顺序：先合的先算，算式里对应的地方都括好了。', 'ok');
      sfx('win');
    } else {
      ui.status.textContent = '只剩一张 ' + fmt(finalCard.v) + '——不是 24';
      ui.status.className = 'st-status is-over';
      renderNote('牌用完了，最后剩 ' + fmt(finalCard.v) + '（' + finalCard.expr + '）。'
        + '点「撤销一步」退回去换条路，或者点「看一种解法」学习本机的走法——这手牌必有解。', 'warn');
      sfx('bad');
    }
    renderCards(); renderOps(); renderSteps(); renderRecord(); renderButtons();
  }

  function undo() {
    if (S.done || !S.steps.length) return;
    /* 步骤与牌面同构：重放这局之前的牌即可——直接从 hand 重建前 n−1 步太绕，
       这里反向恢复：记录每步被吃两牌的快照最简单，代价是每步存 a/b 牌的拷贝 */
    var last = S.steps.pop();
    S.cards = last.snapshot.slice();
    S.undos++;
    S.selCard = -1; S.selOp = '';
    renderCards(); renderOps(); renderSteps(); renderStatus(); renderButtons();
    renderNote('已撤销一步（共撤销 ' + S.undos + ' 次，如实计入结算）：' + last.text + ' 收了回去。', '');
  }

  function showSolution() {
    if (S.done) return;
    var sol = solve(S.hand);
    if (!sol) { renderNote('求解器没找到解——这不该发生（发牌时验证过必有解），请点「换一题」重开。', 'warn'); return; }
    S.done = true; S.gaveUp = true;
    var lines = [];
    sol.forEach(function (st, i) {
      lines.push('第 ' + (i + 1) + ' 步：' + fmt(st.a.v) + ' ' + st.op + ' ' + fmt(st.b.v) + ' = ' + fmt(st.r));
    });
    ui.steps.textContent = '';
    ui.stepsWrap.hidden = false;
    sol.forEach(function (st, i) {
      ui.steps.appendChild(el('li', 'st-d24-step' + (i === sol.length - 1 ? ' is-last' : ''),
        '第 ' + (i + 1) + ' 步：' + fmt(st.a.v) + ' ' + st.op + ' ' + fmt(st.b.v) + ' = ' + fmt(st.r)
        + '　（合成 ' + st.expr + '）'));
    });
    ui.status.textContent = '已看解法 · 这一局不进成绩';
    ui.status.className = 'st-status is-over';
    renderNote('求解器的一种解法如上，整条算式 ' + sol[sol.length - 1].expr + ' = 24——'
      + '每一步把两个数合成一个，先算的先括起来，这就是这道题的运算顺序。'
      + '这手牌共有 ' + S.pathCount + ' 条可行路径，看懂这一条，下一局自己走一条。', 'ok');
    renderCards(); renderOps(); renderButtons();
  }

  function newGame() {
    S.hand = deal(S.level);
    S.pathCount = countPaths(S.hand);
    S.cards = S.hand.map(function (n) {
      return { v: rat(n, 1), expr: String(n), prec: PREC.num };
    });
    S.steps = []; S.undos = 0;
    S.selCard = -1; S.selOp = '';
    S.done = false; S.gaveUp = false;
    S.fracTaught = false; S.negTaught = false;
    ui.status.className = 'st-status';
    renderStatus('发好 ' + S.cards.length + ' 张牌：点一张 → 点运算符 → 再点一张，两两合成 24。');
    renderNote('每一局都经过本机求解器验证必有解——卡住了就换条路或撤销，不是题目无解。', '');
    ui.stepsWrap.hidden = true;
    ui.steps.textContent = '';
    renderCards(); renderOps(); renderButtons();
  }

  /* —— 无解验证器（「无解可证」的落点：自选四数当场穷举）—— */
  function verify() {
    var parts = [];
    for (var i = 0; i < 4; i++) parts.push(ui.verifyInputs[i].value);
    var v = validateNums(parts);
    if (!v.ok) { renderVerify(v.why, 'warn'); return; }
    var sol = solve(v.nums);
    var paths = countPaths(v.nums);
    if (sol) {
      renderVerify('有解——例如 ' + sol[sol.length - 1].expr + ' = 24（'
        + sol.map(function (st) { return fmt(st.a.v) + st.op + fmt(st.b.v); }).join('，再')
        + '）。这四个数共有 ' + paths + ' 条可行路径。', 'ok');
    } else {
      renderVerify('无解：4 个数的两两组合与加减乘除共 3888 条路径全部穷举过，'
        + '没有一条凑得出 24——这四个数真的算不出 24，不是你没想到。', 'warn');
    }
  }
  function renderVerify(msg, tone) {
    ui.verifyOut.textContent = msg;
    ui.verifyOut.className = 'st-d24-verify-out' + (tone ? ' is-' + tone : '');
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    saveRecord({});
    renderRecord();
    renderButtons();
    renderNote('已重置 24 点的本机成绩。', '');
  }

  /* ============ boot ============ */

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.status = root.querySelector('[data-st-status]');
    ui.note = root.querySelector('[data-st-note]');
    ui.levels = root.querySelector('[data-st-d24-levels]');
    ui.board = root.querySelector('[data-st-d24-board]');
    ui.ops = root.querySelectorAll('[data-st-d24-ops] [data-op]');
    ui.stepsWrap = root.querySelector('[data-st-d24-steps-wrap]');
    ui.steps = root.querySelector('[data-st-d24-steps]');
    ui.record = root.querySelector('[data-st-record]');
    ui.undoBtn = root.querySelector('[data-st-undo-step]');
    ui.solutionBtn = root.querySelector('[data-st-solution]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.resetBtn = root.querySelector('[data-st-reset]');
    ui.verifyBtn = root.querySelector('[data-st-d24-verify]');
    ui.verifyOut = root.querySelector('[data-st-d24-verify-out]');
    ui.verifyInputs = root.querySelectorAll('[data-st-d24-vn]');
    if (!ui.board) return;

    restorePrefs();
    renderLevels();
    bindSoundPicker();

    for (var i = 0; i < ui.ops.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          if (b.disabled) return;
          if (!S.cards.length) return;
          S.selOp = b.getAttribute('data-op');
          renderOps();
          renderNote('选了 ' + S.selOp + '——再点一张牌，与选中的 ' + fmt(S.cards[S.selCard].v) + ' 合成。', '');
        });
      })(ui.ops[i]);
    }
    ui.undoBtn.addEventListener('click', undo);
    ui.solutionBtn.addEventListener('click', showSolution);
    ui.newBtn.addEventListener('click', function () { newGame(); });
    ui.resetBtn.addEventListener('click', resetRecord);
    ui.verifyBtn.addEventListener('click', verify);

    newGame();
  }

  /* 音效选择器（口径同组内三作：tool-segmented 双按钮） */
  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          syncSoundPicker();
          save();
          if (S.soundOn) sfx('ok');
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }
  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  global.ST24 = {
    rat: rat,
    fmt: fmt,
    apply: apply,
    exprOf: exprOf,
    PREC: PREC,
    solve: solve,
    countPaths: countPaths,
    deal: deal,
    LEVELS: LEVELS,
    validateNums: validateNums,
  };
})(typeof window !== 'undefined' ? window : this);
